/**
 * Error Boundary Component
 * - Catches React render/lifecycle errors so we don't white-screen.
 * - Reports the error (message + stack + component stack + url + user-agent)
 *   to `/api/admin/client-errors` so admins can diagnose silent UI crashes.
 * - Offers Try Again, Go Back and Go Home — never forces a logout.
 */
import React from 'react';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function detectAppArea() {
  if (typeof window === 'undefined') return 'unknown';
  const p = window.location.pathname || '';
  if (p.startsWith('/admin')) return 'admin';
  if (p.startsWith('/pos')) return 'pos';
  if (p.startsWith('/rider')) return 'rider';
  return 'storefront';
}

function homePathForArea(area) {
  switch (area) {
    case 'admin':
      return '/admin';
    case 'pos':
      return '/pos';
    case 'rider':
      return '/rider';
    default:
      return '/';
  }
}

function tryGetCurrentUserEmail() {
  try {
    const raw = localStorage.getItem('nc_admin_user');
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.email) return u.email;
    }
  } catch { /* ignore */ }
  try {
    const raw = localStorage.getItem('nc_user');
    if (raw) {
      const u = JSON.parse(raw);
      if (u?.email) return u.email;
    }
  } catch { /* ignore */ }
  return null;
}

async function reportError({ error, errorInfo }) {
  if (!BACKEND_URL) return;
  try {
    const area = detectAppArea();
    const payload = {
      message: String(error?.message || error || 'Unknown error'),
      stack: String(error?.stack || '').slice(0, 8000),
      component_stack: String(errorInfo?.componentStack || '').slice(0, 4000),
      url: typeof window !== 'undefined' ? window.location.href : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      user_email: tryGetCurrentUserEmail(),
      app_area: area,
    };
    // Fire-and-forget; use fetch so it works even if axios is the thing that crashed.
    await fetch(`${BACKEND_URL}/api/admin/client-errors`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Reporting must never throw — swallow.
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reported: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
    // Fire-and-forget report
    reportError({ error, errorInfo }).finally(() => {
      this.setState({ reported: true });
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, reported: false });
  };

  handleGoBack = () => {
    // Clear the error first so the previous route can render
    this.setState({ hasError: false, error: null, errorInfo: null, reported: false });
    // If there's a history entry go back, else fall back to section home
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = homePathForArea(detectAppArea());
    }
  };

  handleGoHome = () => {
    // Navigate to the section's home (admin dashboard, storefront home, …).
    // Crucially we do NOT dispatch nc_logout — the user stays signed in.
    const target = homePathForArea(detectAppArea());
    window.location.href = target;
  };

  render() {
    if (this.state.hasError) {
      const area = detectAppArea();
      const homeLabel = area === 'admin' ? 'Admin Dashboard' : area === 'pos' ? 'POS Home' : area === 'rider' ? 'Rider Home' : 'Homepage';
      return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center" data-testid="error-boundary-fallback">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 mb-4">
              This page hit an unexpected error. You&apos;re still signed in — go back or return to the {homeLabel.toLowerCase()} and try again.
            </p>
            <p className="text-[11px] text-gray-400 mb-6" data-testid="error-boundary-reported">
              {this.state.reported ? 'Error reported to the admin diagnostics feed.' : 'Reporting error to admin…'}
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="bg-gray-100 rounded-lg p-4 mb-6 text-left max-h-40 overflow-auto">
                <p className="text-xs font-mono text-red-600 break-all whitespace-pre-wrap">
                  {this.state.error.toString()}
                </p>
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-center">
              <Button variant="outline" onClick={this.handleGoBack} data-testid="error-boundary-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
              <Button variant="outline" onClick={this.handleRetry} data-testid="error-boundary-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button onClick={this.handleGoHome} data-testid="error-boundary-home-btn">
                <Home className="w-4 h-4 mr-2" />
                {homeLabel}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
